import type { FC } from 'react';
import React, { useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Inline, Text, Pressable, xcss } from '@atlaskit/primitives';
import AddIcon from '@atlaskit/icon/core/add';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { IconTile } from '@atlaskit/icon';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { RightSidePanel, ContentWrapper } from '@atlaskit/right-side-panel';

import { SpaceSelectionPanelContent } from './SpaceSelectionPanelContent.tsx';

export type SpaceSelectionRowProps = {
	isExclusion: boolean;
};

const styles = cssMap({
	header: {
		font: token('font.body.large'),
		fontWeight: token('font.weight.medium'),
		display: 'flex',
		flexDirection: 'row',
		paddingTop: token('space.200'),
		paddingBottom: token('space.100'),
	},
	inline: {
		display: 'flex',
		alignItems: 'center',
	},
	asterisk: {
		color: token('color.text.accent.red'),
	},
});

const rowStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	borderRadius: token('radius.small'),
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border.accent.blue',
	marginBottom: 'space.200',
	width: '300px',
	height: '76px',
	paddingLeft: 'space.150',
	backgroundColor: 'elevation.surface',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
	},
	':active': {
		backgroundColor: 'elevation.surface.pressed',
	},
});

const i18n = defineMessages({
	spaceExclusionRowLabel: {
		id: 'bulk-permissions.space.space-exclusion-row.label.non-final',
		defaultMessage: 'Spaces to exclude<asterisk>*</asterisk>',
		description: 'Label for the section of the bulk access page for excluding spaces',
	},
	spaceInclusionRowLabel: {
		id: 'bulk-permissions.space.space-exclusion-row.label.non-final',
		defaultMessage: 'Spaces to include<asterisk>*</asterisk>',
		description: 'Label for the section of the bulk access page for including spaces',
	},
	spaceSelectionButtonTitle: {
		id: 'bulk-permissions.space.space-exclusion-row.title.non-final',
		defaultMessage: 'Add spaces',
		description: 'Header for the section of the bulk access page for excluding spaces',
	},
});

export const SpaceSelectionRow: FC<SpaceSelectionRowProps> = ({ isExclusion }) => {
	const { formatMessage } = useIntl();
	const [isSpaceSelectionPanelOpen, setIsSpaceSelectionPanelOpen] = useState(false);

	const onClick = useCallback(() => {
		//TODO: Experience tracking
		// startExperience();
		if (!isSpaceSelectionPanelOpen) {
			setIsSpaceSelectionPanelOpen(true);
		}

		if (isSpaceSelectionPanelOpen) {
			setIsSpaceSelectionPanelOpen(false);
		}
	}, [setIsSpaceSelectionPanelOpen, isSpaceSelectionPanelOpen]);
	return (
		<>
			<ContentWrapper>
				<Box xcss={styles.header}>
					{formatMessage(isExclusion ? i18n.spaceExclusionRowLabel : i18n.spaceInclusionRowLabel, {
						asterisk: (chunks: React.ReactNode[]) => <Box xcss={styles.asterisk}>{chunks}</Box>,
					})}
				</Box>
				<Pressable xcss={rowStyles} onClick={onClick} testId="space-selection-row">
					<Inline xcss={styles.inline} space="space.100">
						<IconTile icon={AddIcon} label="" appearance="gray" shape="square" size="24" />
						<Text color="color.text.subtle">{formatMessage(i18n.spaceSelectionButtonTitle)}</Text>
					</Inline>
					<ChevronRightIcon label="" color={token('color.icon.accent.blue')} />
				</Pressable>

				<RightSidePanel isOpen={isSpaceSelectionPanelOpen} attachPanelTo="SpaceSelectionPanel">
					<SpaceSelectionPanelContent
						onClose={() => setIsSpaceSelectionPanelOpen(false)}
						isExclusion={isExclusion}
					/>
				</RightSidePanel>
			</ContentWrapper>
		</>
	);
};
